import { nextTick, onBeforeUnmount } from 'vue';

/**
 * Entry animation window of the modal (`slide-up`), used as the reinforcement
 * for the focus. The first attempt runs as soon as the panel renders.
 */
export const DIALOG_INPUT_FOCUS_DELAY_MS = 150;

/**
 * Focuses the modal input when it opens.
 *
 * The first attempt runs on the tick the panel renders, so iOS Safari still
 * treats it as part of the tap and opens the virtual keyboard. Without it the
 * user opens the search and has to tap the input again to type. The timer after
 * the entry animation reinforces the focus, because the browser drops a focus
 * applied while the panel has no layout yet.
 */
export function useDeferredInputFocus(focusTarget: () => void) {
	let timeout: ReturnType<typeof setTimeout> | null = null;
	// `nextTick` runs after `schedule`, so the timeout only exists on the next
	// tick. The generation also drops a scheduling that never became a timer.
	let generation = 0;

	function clearPendingInputFocus() {
		generation += 1;
		if (timeout) {
			clearTimeout(timeout);
			timeout = null;
		}
	}

	function scheduleInputFocus() {
		if (!import.meta.client) return;

		clearPendingInputFocus();
		const currentGeneration = generation;

		nextTick(() => {
			if (currentGeneration !== generation) return;

			focusTarget();
			timeout = setTimeout(() => {
				timeout = null;
				focusTarget();
			}, DIALOG_INPUT_FOCUS_DELAY_MS);
		});
	}

	onBeforeUnmount(clearPendingInputFocus);

	return { scheduleInputFocus, clearPendingInputFocus };
}
